import { idFor } from '@aurora-pvi/seed';
import type { SeededPersona } from './types';

/**
 * The seeded personas behind the identity picker.
 *
 * Six people at five different spans of control, because the single most
 * important thing this list has to prove is that the same call returns
 * genuinely different numbers depending on who asks. A department leader must
 * see a smaller portfolio than the CIO, and an architect must see a portfolio
 * with the money removed — not zeroed.
 *
 * Named for what they are rather than what they were once for. The picker is
 * gated on the server's own `canSwitchIdentity` — where an identity provider is
 * configured there is no picker at all — so these are the people a reviewer can
 * sign in as, not a demonstration prop, and the sign-in screen already calls
 * them seeded personas in the copy a reader sees.
 */

const ALL_MODULES = [
  'portfolio',
  'product-definition',
  'product-360',
  'relationship-mapping',
  'application',
  'delivery-reliability',
  'value',
  'scenario',
  'ingestion',
  'governance',
];

export const SEEDED_PERSONAS: SeededPersona[] = [
  {
    id: idFor('Person', 'PPL-0001'),
    displayName: 'Ingrid Halvorsen',
    roleLabel: 'Chief Information Officer',
    scopeLabel: 'Aurora Airlines — enterprise',
    personaId: 'PER-01',
    scopeId: idFor('Enterprise', 'ENT-AURORA'),
    scopeCode: 'ENT-AURORA',
    scopeType: 'Enterprise',
    allowedModules: ALL_MODULES,
    canSeeRestrictedFinance: true,
    canSeeWorkforce: true,
  },
  {
    id: idFor('Person', 'PPL-0015'),
    displayName: 'Julia Sandberg',
    roleLabel: 'VP, Network & Operations Technology',
    scopeLabel: 'Network & Operations — department',
    personaId: 'PER-05',
    scopeId: idFor('OrganizationalUnit', 'DEP-NOPS'),
    scopeCode: 'DEP-NOPS',
    scopeType: 'Department',
    allowedModules: ALL_MODULES.filter((m) => m !== 'ingestion' && m !== 'governance'),
    canSeeRestrictedFinance: true,
    canSeeWorkforce: true,
  },
  {
    id: idFor('Person', 'PPL-0020'),
    displayName: 'Maria Alvarez',
    roleLabel: 'Senior Director, Crew Operations',
    scopeLabel: 'Crew Operations — value stream',
    personaId: 'PER-06',
    scopeId: idFor('ValueStream', 'VS-CREW'),
    scopeCode: 'VS-CREW',
    scopeType: 'ValueStream',
    allowedModules: [
      'portfolio',
      'product-definition',
      'product-360',
      'relationship-mapping',
      'application',
      'delivery-reliability',
      'value',
      'scenario',
    ],
    canSeeRestrictedFinance: true,
    canSeeWorkforce: true,
  },
  {
    id: idFor('Person', 'PPL-0046'),
    displayName: 'Simone Adeyemi',
    roleLabel: 'Product Owner, Crew Disruption Recovery',
    scopeLabel: 'Crew Disruption Recovery — product',
    personaId: 'PER-08',
    scopeId: idFor('Product', 'PRD-CDR'),
    scopeCode: 'PRD-CDR',
    scopeType: 'Product',
    allowedModules: [
      'portfolio',
      'product-360',
      'relationship-mapping',
      'application',
      'delivery-reliability',
      'value',
    ],
    canSeeRestrictedFinance: true,
    canSeeWorkforce: false,
  },
  {
    // The masking demonstration. Enterprise-wide visibility, no finance
    // entitlement: every cost field comes back null with an Unavailable status
    // and the field names are listed in meta.scope.maskedFields.
    id: idFor('Person', 'PPL-0053'),
    displayName: 'Louis Ferrand',
    roleLabel: 'Domain Architect, Flight Operations',
    scopeLabel: 'Network & Operations — department',
    personaId: 'PER-11',
    scopeId: idFor('OrganizationalUnit', 'DEP-NOPS'),
    scopeCode: 'DEP-NOPS',
    scopeType: 'Department',
    allowedModules: [
      'portfolio',
      'product-definition',
      'product-360',
      'relationship-mapping',
      'application',
      'delivery-reliability',
    ],
    canSeeRestrictedFinance: false,
    canSeeWorkforce: false,
  },
  {
    id: idFor('Person', 'PPL-0007'),
    displayName: 'Nadia Benali',
    roleLabel: 'Head of Data Governance',
    scopeLabel: 'Aurora Airlines — enterprise',
    personaId: 'PER-18',
    scopeId: idFor('Enterprise', 'ENT-AURORA'),
    scopeCode: 'ENT-AURORA',
    scopeType: 'Enterprise',
    allowedModules: ALL_MODULES,
    canSeeRestrictedFinance: false,
    canSeeWorkforce: false,
  },
  {
    /*
      The platform administrator, PER-25.

      Added because "administrator" and "chief information officer" had been
      the same identity: PER-01 held `administer`, so the CIO landed on Roles &
      access able to widen their own entitlements. The two are now separate
      people with separate roles, and this is the one that holds every
      permission.

      The id is `iam.person.id` — the value the X-PVI-Person header carries —
      rather than a derived entity id, because this person is seeded directly
      into `iam.person` by migration 0041.
    */
    id: '9f000000-0000-4000-8000-00000000ad11',
    displayName: 'Priya Anand',
    roleLabel: 'Platform Administrator',
    scopeLabel: 'Aurora Airlines — enterprise',
    personaId: 'PER-25',
    scopeId: idFor('Enterprise', 'ENT-AURORA'),
    scopeCode: 'ENT-AURORA',
    scopeType: 'Enterprise',
    allowedModules: ALL_MODULES,
    canSeeRestrictedFinance: true,
    canSeeWorkforce: true,
  },
];

export function findSeededPersona(id: string): SeededPersona | undefined {
  return SEEDED_PERSONAS.find((user) => user.id === id);
}

export const DEFAULT_PERSONA: SeededPersona = SEEDED_PERSONAS[0] as SeededPersona;
